import { useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  RichTextContent,
  useCollectionBinding,
  useEditMode,
} from '@vibesites/runtime/react';
import { stripHtml } from './AmenitiesShared';

interface Review {
  name?: string | null;
  title?: string | null;
  rating?: string | number | null;
  text?: string | null;
  date?: string | null;
  source?: string | null;
}

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'false' && s !== 'null' && s !== 'undefined';
}

const AUTO_ADVANCE_MS = 7000;
// Number of lines a review is clamped to before "View more" appears. Keeps the
// carousel height stable regardless of how long or short each review is.
const CLAMP_LINES = 6;

export function ReviewsCarousel() {
  const reviews = useCollectionBinding<Review>('reviewscarousel.items', {
    collection: 'reviews',
  });

  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  const count = reviews?.length ?? 0;
  const [index, setIndex] = useState(0);
  const safeIndex = count > 0 ? ((index % count) + count) % count : 0;

  // Which review (if any) the visitor has expanded past the clamp.
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  // Indices whose text overflows the clamp and therefore need a "View more".
  const [overflowing, setOverflowing] = useState<Set<number>>(new Set());
  const quoteRefs = useRef<Array<HTMLQuoteElement | null>>([]);

  const goTo = useCallback(
    (next: number) => {
      if (count === 0) return;
      setExpandedIdx(null);
      setIndex(((next % count) + count) % count);
    },
    [count],
  );

  // Auto-cycle through the reviews. Re-runs (and resets the timer) whenever the
  // active index changes — including after manual arrow navigation. Paused while
  // a review is expanded so the visitor can finish reading it.
  useEffect(() => {
    if (count <= 1 || expandedIdx !== null) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, AUTO_ADVANCE_MS);
    return () => window.clearInterval(id);
  }, [count, index, expandedIdx]);

  // Measure which clamped reviews overflow, so "View more" only shows when
  // there's actually hidden text. Re-measures on resize (line wrapping changes).
  useEffect(() => {
    const measure = () => {
      const next = new Set<number>();
      quoteRefs.current.forEach((el, i) => {
        if (el && el.scrollHeight - el.clientHeight > 1) next.add(i);
      });
      setOverflowing(next);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [count]);

  // Auto-hide when there are no reviews and we're not editing.
  if (count === 0 && !isEditMode) return null;

  return (
    <section className="py-16 md:py-24 bg-white text-black">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Subtitle — defaults to the property name */}
        <RichTextContent
          path="reviewscarousel.subtitle"
          label="Reviews Subtitle"
          defaultValue='<p><span data-type="field-token" data-field="property_name"></span></p>'
          as="div"
          className="font-body text-sm tracking-[0.25em] uppercase text-black/70"
        />

        {/* Divider line */}
        <div className="w-16 h-px bg-black/40 mx-auto my-6" aria-hidden="true" />

        {/* Full title */}
        <RichTextContent
          path="reviewscarousel.title"
          label="Reviews Title"
          defaultValue="<p>Take Our Residents Word For It</p>"
          as="h2"
          className="font-heading text-3xl md:text-5xl leading-tight"
        />

        {count > 0 ? (
          <div className="relative mt-12 md:mt-20">
            {count > 1 && (
              <button
                type="button"
                onClick={() => goTo(safeIndex - 1)}
                aria-label="Previous review"
                className="absolute left-0 top-1/2 -translate-y-1/2 p-2 text-black/50 hover:text-black transition-colors"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}

            <div className="overflow-hidden mx-8 md:mx-16">
              <div
                className="flex items-stretch transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${safeIndex * 100}%)` }}
              >
                {reviews?.map((review, i) => (
                  <div
                    key={i}
                    className="w-full flex-shrink-0 min-h-[14rem] flex flex-col items-center justify-center px-2"
                    aria-hidden={i !== safeIndex}
                  >
                    {hasValue(review.text) && (
                      <blockquote
                        ref={(el) => {
                          quoteRefs.current[i] = el;
                        }}
                        className="font-body text-sm italic leading-relaxed max-w-xl mx-auto"
                        style={
                          expandedIdx === i
                            ? undefined
                            : {
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: CLAMP_LINES,
                                overflow: 'hidden',
                              }
                        }
                      >
                        &ldquo;{stripHtml(review.text)}&rdquo;
                      </blockquote>
                    )}
                    {overflowing.has(i) && (
                      <button
                        type="button"
                        onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
                        className="font-body text-xs uppercase tracking-wider underline underline-offset-4 mt-4 text-black/60 hover:text-black transition-colors"
                      >
                        {expandedIdx === i ? 'Read less' : 'Read more'}
                      </button>
                    )}
                    {hasValue(review.name) && (
                      <p className="font-body text-sm mt-6">{review.name}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {count > 1 && (
              <button
                type="button"
                onClick={() => goTo(safeIndex + 1)}
                aria-label="Next review"
                className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-black/50 hover:text-black transition-colors"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>
        ) : (
          <div className="mt-12 py-12 text-black/50">
            <p className="font-body text-lg">
              No reviews yet. Add reviews to this property to display them in this carousel.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ReviewsCarousel',
  displayName: 'Reviews Carousel',
  tokens: {
    'reviewscarousel.subtitle': {
      label: 'Reviews Subtitle',
      type: 'richtext',
      defaultValue: '<p><span data-type="field-token" data-field="property_name"></span></p>',
    },
    'reviewscarousel.title': {
      label: 'Reviews Title',
      type: 'richtext',
      defaultValue: '<p>Take Our Residents Word For It</p>',
    },
  },
  collectionBindings: {
    'reviewscarousel.items': {
      label: 'Reviews',
      collection: 'reviews',
    },
  },
};

(ReviewsCarousel as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ReviewsCarousel;
